/**
 * Qinggong (วิชาตัวเบา): jumping, the air, landing, the dash and the arts — the take-off crouch and
 * push, the legs reaching for the ground, a step off the air, the dash's coil and brake, the stride's
 * two steps (for stopping and landing into a run), the glide, skimming water, running up a wall,
 * riding the flying sword and standing balanced on the water. Motions picked in
 * render/qinggongAnim.ts.
 */
import { WALK_FRAMES, type Rig, type MotionDef, type PoseRigs } from './kit';
import { rigFor } from './index';

const step = (_ph: number, pose: 'stepA' | 'stepB'): Partial<Rig> => {
  // The stride's two moments with both feet down and apart (a quarter cycle either side of the
  // legs passing), so stopping or landing into a run starts from a real frame of the walk.
  const at = pose === 'stepA' ? 0 : WALK_FRAMES / 2;
  return { ...rigFor('walk', at) };
};

const skim = (_ph: number, pose: 'skimA' | 'skimB'): Partial<Rig> => {
  // วิ่งบนน้ำ: a long gliding step, the front toe just touching the water, the back leg trailing
  // high behind; light and upright, arms out for balance.
  const a = pose === 'skimA';
  return {
    o: 1,
    lean: 1,
    tilt: 8,
    f1: a ? -9 : 8,
    l1: a ? 4 : 0,
    f2: a ? 8 : -9,
    l2: a ? 0 : 4,
    hem: 2,
    flare: 1,
    sway: 2.4,
    arm: 'out',
    far: 'back',
  };
};

const climb = (_ph: number, pose: 'climbA' | 'climbB'): Partial<Rig> => {
  // ไต่กำแพง: running straight up the wall it faces — the body pitched in against it, one knee
  // driven high to the next step, the other leg pushing off below, the blade carried high (clear
  // of the stepping legs), the far arm streaming back.
  const a = pose === 'climbA';
  return {
    o: 2,
    lean: 2,
    tilt: 20,
    f1: a ? -3 : 3,
    l1: a ? 2 : 10,
    f2: a ? 3 : -3,
    l2: a ? 10 : 2,
    hem: 1,
    sway: 2.5,
    arm: 'raise',
    far: 'back',
    face: 'block',
  };
};

const ride = (_ph: number, pose: 'rideA' | 'rideB'): Partial<Rig> => {
  // Standing on the flying sword: feet apart along the blade, knees soft, the weight rocking
  // from heel to toe and the arms keeping the balance.
  const a = pose === 'rideA';
  return {
    o: a ? 2 : 1,
    lean: a ? 1 : -1,
    tilt: a ? 4 : -2,
    f1: -5,
    f2: 4,
    hem: 1,
    sway: a ? 1.6 : 0.8,
    arm: 'hang',
    far: a ? 'hang' : 'back',
  };
};

/** This group's poses. */
export type Poses =
  | 'stepA'
  | 'stepB'
  | 'crouch'
  | 'push'
  | 'reach'
  | 'airStep'
  | 'dashGo'
  | 'dashEnd'
  | 'glide'
  | 'skimA'
  | 'skimB'
  | 'skimMid'
  | 'hover'
  | 'climbA'
  | 'climbB'
  | 'rideA'
  | 'rideB'
  | 'jump'
  | 'rise'
  | 'fall'
  | 'land'
  | 'dash';

export const POSES: PoseRigs<Poses> = {
  stepA: step,
  stepB: step,
  crouch: () => {
    // Gathering for the leap: sunk deep on both knees, feet under the hips, the body folded over
    // them, arms drawn down and back to throw up with the spring.
    return {
      o: 9,
      lean: 1,
      tilt: 18,
      headDx: 1,
      f1: -4,
      f2: 4,
      hem: 0,
      sway: 0.5,
      arm: 'low',
      far: 'back',
      face: 'block',
    };
  },
  push: () => {
    // The push-off: legs straightened right through to the toes, body long and upright, the arms
    // flung up; hair and hems dragged down by the rise.
    return {
      o: -1,
      tilt: 2,
      f1: -3,
      l1: 3,
      f2: 2,
      l2: 1,
      hem: 0,
      sway: 0.5,
      arm: 'raise',
      far: 'up',
    };
  },
  reach: () => {
    // About to land: both legs let down and reaching for the ground, the front one ahead to take
    // the weight, arms out for balance; the hems blown up by the drop.
    return {
      o: 0,
      lean: -1,
      tilt: -4,
      f1: -5,
      l1: 2,
      f2: 5,
      l2: 0,
      hem: 5,
      flare: 3,
      sway: 2,
      arm: 'out',
      far: 'back',
    };
  },
  airStep: () => {
    // เหยียบเมฆา: one foot stamped straight down onto the cloud, the other knee driven up high,
    // the arms thrown out wide to lift off it.
    return {
      o: 1,
      tilt: 4,
      f1: -1,
      l1: 0,
      f2: 4,
      l2: 10,
      hem: 2,
      flare: 2,
      sway: 1,
      arm: 'out',
      far: 'up',
    };
  },
  dashGo: () => {
    // Coiling into the dart: dropped low over the front foot, the blade held up before the body
    // (trailed low behind, it cut through the back leg), the far arm swept back.
    return {
      o: 6,
      lean: 2,
      tilt: 18,
      f1: -7,
      f2: 5,
      hem: 0,
      sway: 1,
      arm: 'guard',
      far: 'back',
      face: 'block',
    };
  },
  dashEnd: () => {
    // Braking out of the dart: the front foot planted far ahead, the body rocked back over it,
    // hair and hems still flying forward past it.
    return {
      o: 4,
      lean: -1,
      tilt: -2,
      f1: -8,
      f2: 7,
      hem: 2,
      flare: 2,
      sway: -1.5,
      arm: 'low',
      far: 'back',
      face: 'block',
    };
  },
  glide: () => {
    // ร่อนลม: arms spread wide like wings, the legs together and trailing, the body laid forward
    // into the drift; the robe billowing up from below.
    return {
      o: 0,
      lean: 1,
      tilt: 10,
      f1: -4,
      l1: 5,
      f2: 1,
      l2: 6,
      hem: 6,
      flare: 5,
      sway: 3,
      arm: 'out',
      far: 'up',
    };
  },
  skimA: skim,
  skimB: skim,
  skimMid: () => {
    // Between steps: both feet off the surface, the legs passing, the body at the top of its float.
    return {
      o: -1,
      lean: 1,
      tilt: 8,
      f1: -1,
      l1: 3,
      f2: 1,
      l2: 5,
      hem: 3,
      flare: 1,
      sway: 2.6,
      arm: 'out',
      far: 'back',
    };
  },
  hover: () => {
    // Standing on the water: balanced on one foot, the other drawn up, arms open.
    return { o: 0, tilt: 2, f1: -2, f2: 2, l2: 5, hem: 2, sway: 1, arm: 'out', far: 'up' };
  },
  climbA: climb,
  climbB: climb,
  rideA: ride,
  rideB: ride,
  jump: () => {
    // The top of the leap: tucked, both knees drawn up high under the body, the body curled over
    // them; weightless for a moment, the hems and hair floating out.
    return {
      o: 3,
      tilt: 12,
      f1: -3,
      f2: 3,
      l1: 9,
      l2: 11,
      hem: 4,
      flare: 2,
      sway: 1.8,
      arm: 'out',
      far: 'up',
    };
  },
  rise: () => {
    // Driving upward: the front knee up high, the back leg trailing below. The blade is carried
    // high behind the shoulder (held low behind, it ran through the trailing shin); hair and hems
    // pressed down by the climb.
    return { tilt: 6, f1: -5, l1: 3, f2: 4, l2: 9, hem: 1, sway: 0.8, arm: 'raise', far: 'back' };
  },
  fall: () => {
    // Coming down: the legs loosening under the body, one ahead, arms out for balance; the hems
    // blown up and the hair streaming up by the drop.
    return {
      tilt: -6,
      f1: -4,
      l1: 3,
      f2: 4,
      l2: 4,
      hem: 6,
      flare: 4,
      sway: 3,
      arm: 'out',
      far: 'up',
    };
  },
  land: () => {
    // Absorbing the landing: a deep crouch, knees well bent, the body pitched forward; the hems
    // and hair still settling down past it.
    return {
      o: 7,
      lean: 1,
      tilt: 18,
      headDx: 1,
      f1: -6,
      f2: 5,
      hem: 2,
      flare: 1,
      arm: 'low',
      far: 'back',
      face: 'block',
      sway: -0.5,
    };
  },
  dash: () => {
    // Darting low: a long, flat bound, the back foot just leaving the ground, the blade held up
    // clear of the legs.
    return {
      o: 5,
      lean: 2,
      tilt: 22,
      f1: -11,
      f2: 7,
      l1: 2,
      hem: 1,
      sway: 2.5,
      arm: 'guard',
      far: 'back',
      face: 'block',
    };
  },
};

export const MOTIONS = [
  /** Landing: the absorb, then up into the stance at ease, the fighting stance, or the stride. */
  { id: 'land', keys: ['land', 'idle'], ease: ['out'] },
  { id: 'landReady', keys: ['land', 'ready'], ease: ['out'] },
  { id: 'landRun', keys: ['land', 'stepA'], ease: ['out'] },
  /** Take-off: sunk into a crouch, then driven up through the legs into the rising pose. */
  { id: 'takeoff', keys: ['crouch', 'push', 'rise'], ease: ['in', 'out'] },
  /** In the air, by the fall: rising, tucked at the top, falling, then the legs reaching down. */
  { id: 'air', keys: ['rise', 'jump', 'fall', 'reach'], ease: ['out', 'inOut', 'in'] },
  /** A step off the air (เหยียบเมฆา): one foot stamped down on the cloud, the knee driven up. */
  { id: 'airStep', keys: ['airStep', 'rise'], ease: ['out'] },
  /** The dash: coiled, darting flat out, braking with the front foot. */
  { id: 'dash', keys: ['dashGo', 'dash', 'dashEnd'], ease: ['out', 'in'] },
  { id: 'dashSettle', keys: ['dashEnd', 'ready'], ease: ['inOut'] },
  /** Stopping: from the stride's nearer step down into the stance at ease. */
  { id: 'stopA', keys: ['stepA', 'idle'], ease: ['out'] },
  { id: 'stopB', keys: ['stepB', 'idle'], ease: ['out'] },
  /** ร่อนลม: the arms opened from the fall into the spread glide. */
  { id: 'glideOpen', keys: ['fall', 'glide'], ease: ['out'] },
  /** วิ่งบนน้ำ: long light strides, the toes only brushing the water, arms out for balance. */
  {
    id: 'skim',
    keys: ['skimA', 'skimMid', 'skimB', 'skimMid'],
    ease: ['in', 'out', 'in', 'out'],
    loop: true,
  },
  /** ไต่กำแพง: high quick steps up the face, the body pressed in, arms streaming back. */
  { id: 'climb', keys: ['climbA', 'climbB'], ease: ['inOut', 'inOut'], loop: true },
  /** Riding the flying sword: the weight rocking heel to toe, the arms keeping the balance. */
  { id: 'ride', keys: ['rideA', 'rideB'], ease: ['inOut', 'inOut'], loop: true },
] as const satisfies readonly MotionDef[];
